// A patient's registration details: name, date of birth, NHI, contacts, address, demographics, next of kin and
// (AU) Medicare, DVA and private insurance. Used to register a patient and edit one on the dashboard, and to
// complete an imported patient's details in its review.
//
// It edits the patient it is given in place and does not save. The page that owns the save asks it to
// prepareForCreate() or prepareForUpdate() first: the address the user picked from the lookup and the date of
// birth's day/month/year fields are form state rather than fields of the patient, and those calls write them
// back onto the patient (and say no when an NZ address was not chosen from the lookup).
//
// onReady({ form }) hands the owner that API once the form is up.
app.component("patientDetailsForm", {
	templateUrl: "app/src/js/components/patientDetailsForm/patientDetailsForm.html",
	controller: "patientDetailsFormController",
	bindings: {
		// The patient being edited, in place.
		patient: "<",
		// The patient as last saved, which the verified pills compare against. Defaults to CurrentPatient's.
		savedPatient: "<",
		// Every section at once, rather than revealed as the one before it is filled. For a patient whose details
		// are being completed rather than entered from scratch, where any section may hold what is missing.
		showAllSections: "<",
		onReady: "&",
	},
});

app.controller("patientDetailsFormController", [
	"$scope",
	"$element",
	"$modal",
	"$mdDialog",
	"CurrentPatient",
	"Session",
	"PatientAPI",
	"NHI",
	"NHISearchPatientsAPI",
	"PlaceAPI",
	"NgTableParams",
	"NHI_SERVICE_ERROR",
	"NHI_VERSION_V2",
	"CONFIG",
	"ENV",
	"countryConfig",
	"AUSTRALIAN_HEALTH_FUND_TYPE",
	"AUSTRALIAN_HEALTH_FUND_TYPE_TEST",
	"AU_HOSPITAL_COVER_TIER_TYPE",
	"AU_DVA_CARD_TYPE",
	"AU_INSURANCE_POLICY_STATUS_TYPE",
	"AU_OVERSEAS_COVER_TYPE",
	function (
		$scope,
		$element,
		$modal,
		$mdDialog,
		CurrentPatient,
		Session,
		PatientAPI,
		NHI,
		NHISearchPatientsAPI,
		PlaceAPI,
		NgTableParams,
		NHI_SERVICE_ERROR,
		NHI_VERSION_V2,
		CONFIG,
		ENV,
		countryConfig,
		AUSTRALIAN_HEALTH_FUND_TYPE,
		AUSTRALIAN_HEALTH_FUND_TYPE_TEST,
		AU_HOSPITAL_COVER_TIER_TYPE,
		AU_DVA_CARD_TYPE,
		AU_INSURANCE_POLICY_STATUS_TYPE,
		AU_OVERSEAS_COVER_TYPE
	) {
		const ctrl = this;

		// The template binds to plain scope names (it began life as an ng-include on the dashboard), so the
		// state and helpers it uses live on $scope, and `patient` follows the binding.
		$scope.CP = CurrentPatient;
		$scope.PA = PatientAPI;
		$scope.NHIAPI = NHISearchPatientsAPI;
		$scope.PlaceAPI = PlaceAPI;
		$scope.countryConfig = countryConfig;
		$scope.NHI_VERSION_V2 = NHI_VERSION_V2;
		// The staging-only test fund is offered on localhost and dev only, so an ECLIPSE claim can be run end
		// to end against Tyro's "TST" brand code. Never in production - see ENV.TEST_DATA_ENABLED.
		$scope.auHealthFunds = ENV.TEST_DATA_ENABLED
			? AUSTRALIAN_HEALTH_FUND_TYPE.concat(AUSTRALIAN_HEALTH_FUND_TYPE_TEST)
			: AUSTRALIAN_HEALTH_FUND_TYPE;
		$scope.dvaCardTypes = AU_DVA_CARD_TYPE;
		$scope.auPolicyStatuses = AU_INSURANCE_POLICY_STATUS_TYPE;
		$scope.auHospitalCoverLevels = AU_HOSPITAL_COVER_TIER_TYPE;
		$scope.auOverseasCoverTypes = AU_OVERSEAS_COVER_TYPE;
		$scope.currentYear = new Date().getFullYear();

		$scope.patient = {};
		$scope.status = { open: false };
		$scope.badNHI = false;
		$scope.medicareExpiryPopup = { opened: false };
		$scope.optionalPatients = [];

		// Form state that is not a field of the patient: the address as picked from the lookup (an object) or
		// as typed (a string), and the date of birth as three boxes. Written back by prepareForCreate/Update.
		$scope.display = { address: "", apartmentNumber: "" };
		$scope.showingDate = { YYYY: undefined, MM: undefined, DD: undefined };
		$scope.autocompleteOptions = {};
		// The address the record was loaded with, so an update only rewrites the address when it was changed.
		let startAddress = "";

		$scope.isNHIFeatureEnabled = false;
		$scope.fhirNhiFeature = false;
		const features = Session.practice && Session.practice.practiceFeatures;
		if (features) {
			if (features.fhirNhiFeature) $scope.fhirNhiFeature = features.fhirNhiFeature;
			if (features.nhiFeature === true || features.fhirNhiFeature === true) $scope.isNHIFeatureEnabled = true;
		}
		if (countryConfig.countryCode === "NZ") {
			$scope.autocompleteOptions = { componentRestrictions: { country: "nz" }, types: ["geocode"] };
		} else if (countryConfig.countryCode === "AU") {
			$scope.autocompleteOptions = { componentRestrictions: { country: "au" }, types: ["geocode"] };
		}

		ctrl.$onChanges = function (changes) {
			if (changes.patient) {
				$scope.patient = ctrl.patient || {};
				syncFormStateFromPatient();
			}
		};

		ctrl.$onInit = function () {
			ctrl.onReady({
				form: {
					prepareForCreate: prepareForCreate,
					prepareForUpdate: prepareForUpdate,
					sync: syncFormStateFromPatient,
					// The date of birth as typed so far, for the owner's duplicate check while registering.
					getShowingDate: function () {
						return $scope.showingDate;
					},
					// Whether the address in the field was picked from the lookup, before any prepareFor... call
					// has written it onto the patient.
					hasLookedUpAddress: hasLookedUpAddress,
				},
			});
		};

		$scope.$watch("patient.dateOfBirth", function (newValue) {
			if (newValue) {
				var dob = new Date($scope.patient.dateOfBirth);
				$scope.showingDate = { DD: dob.getDate(), MM: dob.getMonth() + 1, YYYY: dob.getFullYear() };
			}
		}, true);

		$scope.$watch("patient.addresses", function (newValue) {
			if (newValue && newValue[0] && newValue[0].json) {
				$scope.display.address = JSON.parse(newValue[0].json);
				startAddress = newValue[0].line1;
				$scope.display.apartmentNumber = newValue[0].apartmentNumber ? newValue[0].apartmentNumber : "";
			}
		}, true);

		/**
		 * Puts the form's own state back in step with the patient it is editing.
		 *
		 * The address and the day/month/year fields are built from the patient rather than bound to it, so
		 * nothing follows the patient being replaced. Runs whenever the patient underneath is swapped - a
		 * record loaded, cancelled edits, another user's change arriving.
		 */
		function syncFormStateFromPatient() {
			const patient = $scope.patient;
			$scope.addressError = false;
			$scope.showingDate = { YYYY: undefined, MM: undefined, DD: undefined };
			$scope.display.address = "";
			$scope.display.apartmentNumber = "";
			startAddress = "";

			// A saved patient always has an address row for the form to fill; one being registered gets it at save.
			if (patient.id && (!patient.addresses || patient.addresses.length === 0)) {
				patient.addresses = [{ line1: "", postCode: 1111, type: "PHYSICAL", apartmentNumber: "" }];
			}
			if (patient.addresses && patient.addresses[0]) {
				$scope.display.address = patient.addresses[0].line1 || "";
				$scope.display.apartmentNumber = patient.addresses[0].apartmentNumber || "";
				startAddress = patient.addresses[0].line1 || "";
			}

			if (patient.dateOfBirth) {
				var dob = new Date(patient.dateOfBirth);
				$scope.showingDate = { DD: dob.getDate(), MM: dob.getMonth() + 1, YYYY: dob.getFullYear() };
			}
		}

		// ==========================================
		// SAVE PREPARATION
		// ==========================================

		/**
		 * Writes the form state onto a patient about to be registered. False when it can't be saved yet: an NZ
		 * address that was typed rather than chosen from the lookup (the field is selected and flagged).
		 */
		function prepareForCreate() {
			const patient = $scope.patient;
			patient.dateOfBirth = makeDateString();

			if (!patient.addresses) patient.addresses = [{}];

			if ($scope.display.address && $scope.display.address.formatted_address) {
				patient.addresses[0].line1 = $scope.display.address.formatted_address;
			} else if (typeof $scope.display.address === "string" && $scope.display.address.trim()) {
				// Typed without picking a suggestion. NZ is refused below for want of coordinates; elsewhere
				// the typed address is kept rather than silently dropped.
				patient.addresses[0].line1 = $scope.display.address.trim();
			}
			patient.addresses[0].type = "PHYSICAL";
			patient.addresses[0].line3 = null;
			patient.addresses[0].line4 = null;
			if ($scope.display.apartmentNumber) {
				patient.addresses[0].apartmentNumber = $scope.display.apartmentNumber;
			}
			patient.addresses[0].json = JSON.stringify($scope.display.address);

			if ($scope.display.address instanceof Object) {
				mapAddressComponents($scope.display.address.address_components, patient.addresses[0]);
			}

			if (!addressIsSavable(patient.addresses[0])) {
				return false;
			}

			removeEmptyStringValues();
			return true;
		}

		/**
		 * Writes the form state onto a saved patient about to be updated. The address is only rewritten when it
		 * was changed. False when it can't be saved yet, as for prepareForCreate().
		 */
		function prepareForUpdate() {
			const patient = $scope.patient;
			if (!patient.addresses || patient.addresses.length === 0) {
				patient.addresses = [{ line1: "", postCode: 1111, type: "PHYSICAL", apartmentNumber: "" }];
			}

			if (startAddress != $scope.display.address) {
				if (!$scope.display.address) {
					patient.addresses[0].line1 = "";
					patient.addresses[0].json = null;
				} else if ($scope.display.address instanceof Object) {
					patient.addresses[0].line1 = $scope.display.address.formatted_address;
					patient.addresses[0].json = JSON.stringify($scope.display.address);
					patient.addresses[0].type = patient.addresses[0].type || "PHYSICAL";
					mapAddressComponents($scope.display.address.address_components, patient.addresses[0]);
				} else {
					selectAddressInput();
					$scope.addressError = true;
					return false;
				}
			}

			if (!addressIsSavable(patient.addresses[0])) {
				return false;
			}

			patient.addresses[0].apartmentNumber = $scope.display.apartmentNumber;

			// new Date() of anything is still a Date, so check the parts themselves.
			if ($scope.showingDate.YYYY && $scope.showingDate.MM && $scope.showingDate.DD) patient.dateOfBirth = makeDateString();

			removeEmptyStringValues();
			return true;
		}

		/**
		 * A NZ patient's DHB is resolved from the coordinates the address lookup returns, and the API rejects
		 * a NZ patient without them. That covers an address typed without selecting a suggestion, an address
		 * that was emptied, and a record saved before the lookup payload was stored - all of which need the
		 * user to pick the address again rather than a server error.
		 */
		function addressIsSavable(address) {
			if (countryConfig.countryCode !== "NZ") {
				return true;
			}

			if (!hasLookupCoordinates(address)) {
				selectAddressInput();
				$scope.addressError = true;
				return false;
			}

			return true;
		}

		function hasLookupCoordinates(address) {
			if (!address || !address.json) {
				return false;
			}

			try {
				const payload = JSON.parse(address.json);
				const location = payload && payload.geometry && payload.geometry.location;
				return !!location && typeof location.lat === "number" && typeof location.lng === "number";
			} catch (e) {
				return false;
			}
		}

		// The field holds the lookup's place (or the saved one, parsed back) once an address is picked, and a
		// string while one is only typed. A live place's lat/lng are functions; a saved one's are numbers.
		function hasLookedUpAddress() {
			const address = $scope.display.address;
			const location = address instanceof Object && address.geometry && address.geometry.location;
			if (!location) {
				return false;
			}
			const lat = typeof location.lat === "function" ? location.lat() : location.lat;
			const lng = typeof location.lng === "function" ? location.lng() : location.lng;
			return typeof lat === "number" && typeof lng === "number";
		}

		// This form's own address field: there can be more than one form on the page (the dashboard's and a
		// review over it), so not document.querySelector.
		function selectAddressInput() {
			const addressInput = $element[0].querySelector("#ct-address");
			if (addressInput) {
				addressInput.select();
			}
		}

		function removeEmptyStringValues() {
			if ($scope.patient.phoneNumber === "") $scope.patient.phoneNumber = null;
			if ($scope.patient.mobileNumber === "") $scope.patient.mobileNumber = null;
			if ($scope.patient.email === "") $scope.patient.email = null;
		}

		function makeDateString() {
			return $scope.showingDate.YYYY + "-" + $scope.showingDate.MM + "-" + $scope.showingDate.DD;
		}

		function mapAddressComponents(components, targetAddress) {
			(components || []).map(function (obj) {
				if (obj.types.indexOf("postal_code") !== -1) targetAddress.postCode = obj.long_name;
				if (obj.types.indexOf("country") !== -1) targetAddress.country = obj.long_name;
				if (obj.types.indexOf("locality") !== -1) targetAddress.city = obj.long_name;
				if (obj.types.indexOf("sublocality") !== -1) targetAddress.line2 = obj.long_name;
			});
		}

		// ==========================================
		// SECTIONS, VERIFICATION
		// ==========================================

		// Gated on the identity fields only. Contact details are in column one alongside the name and date of
		// birth, so this cannot hide the fields needed to supply them. Which of them are required is enforced
		// by the fields themselves, not by hiding them.
		$scope.isSectionTwoVisible = function () {
			return ctrl.showAllSections || countryConfig.medicareFeatureEnabled || ($scope.showingDate.DD && $scope.showingDate.MM && $scope.showingDate.YYYY && $scope.patient.firstName && $scope.patient.lastName);
		};

		$scope.isSectionThreeVisible = function () {
			return ctrl.showAllSections || countryConfig.medicareFeatureEnabled || ($scope.isSectionTwoVisible() && $scope.display.address);
		};

		$scope.isSectionFourVisible = function () {
			return ctrl.showAllSections || countryConfig.medicareFeatureEnabled || ($scope.isSectionThreeVisible() && $scope.patient.gender && $scope.patient.codedEthnicities && $scope.patient.isInterpreterNeeded !== null && $scope.patient.isResident !== null);
		};

		// Verification state comes from CurrentPatient, which is also what the patient header's comms icons
		// read. The rules for it are subtle enough - a verify date only counts while the value still matches the
		// saved one - that a second copy here would be a second thing to get wrong.
		function saved() {
			return ctrl.savedPatient || undefined;
		}

		$scope.isEmailVerified = function () {
			return CurrentPatient.isEmailVerified($scope.patient, saved());
		};

		$scope.isMobileVerified = function () {
			return CurrentPatient.isMobileVerified($scope.patient, saved());
		};

		$scope.emailVerifiedTitle = function () {
			return CurrentPatient.emailVerifiedTitle($scope.patient, saved());
		};

		$scope.mobileVerifiedTitle = function () {
			return CurrentPatient.mobileVerifiedTitle($scope.patient, saved());
		};

		// ==========================================
		// INSURANCE, MEDICARE, DVA, NEXT OF KIN
		// ==========================================
		$scope.addInsurancePolicy = function () {
			if (!$scope.patient.au_private_health_insurance) $scope.patient.au_private_health_insurance = [];
			$scope.patient.au_private_health_insurance.push({
				health_fund_name: "", policy_status: "ACTIVE", membership_number: "", hospital_cover_level: "",
				hospital_notes: "", extras_cover_level: "", extras_notes: "", is_open: true,
			});
		};

		$scope.removeInsurancePolicy = function (index) {
			$scope.patient.au_private_health_insurance.splice(index, 1);
		};

		// Medicare and DVA are removed by clearing the whole record rather than by blanking the boxes. A record
		// left in place with empty values still reads as "this patient holds a Medicare card, we just do not have
		// the number", which is a different thing from not holding one - and it is the distinction ECLIPSE
		// claiming and verification are built on.

		/**
		 * Whether there is anything recorded to clear. Counts the IHI too, even though this form cannot edit it:
		 * the name, card number, IRN and expiry are all NOT NULL on the record, so a Medicare row carrying nothing
		 * but an IHI cannot be saved - and one already holding it needs the Clear button offered rather than
		 * hidden, since there is no other way to get rid of an unsavable record.
		 */
		$scope.hasMedicareDetails = function () {
			const medicare = $scope.patient.medicare_details;
			if (!medicare) return false;
			return !!(medicare.name_on_card || medicare.card_number || medicare.irn || medicare.expiry_date || medicare.ihi);
		};

		$scope.hasDvaDetails = function () {
			const dva = $scope.patient.dva_details;
			if (!dva) return false;
			return !!(dva.file_number || dva.card_type || dva.accepted_disabilities || dva.expiry_date);
		};

		// Cleared as a whole rather than field by field, for the reason above: the record cannot be saved
		// half-empty, so the choice is the whole card or none of it. Typing in any of the boxes afterwards
		// rebuilds the object, so this is undoable without reloading the patient.
		$scope.clearMedicareDetails = function () {
			$scope.patient.medicare_details = null;
		};

		$scope.clearDvaDetails = function () {
			$scope.patient.dva_details = null;
		};

		$scope.openMedicareExpiry = function () {
			$scope.medicareExpiryPopup.opened = true;
		};

		$scope.addNextOfKin = function () {
			if (!$scope.patient.nextOfKin) $scope.patient.nextOfKin = [];
			$scope.patient.nextOfKin.push({ kinName: ``, kinRelationship: ``, kinContactPhone: ``, kinContactEmail: `` });
		};

		$scope.removeNextOfKin = function (index) {
			$scope.patient.nextOfKin.splice(index, 1);
		};

		// ==========================================
		// NHI LOOKUP
		// ==========================================
		$scope.validateNHI = function (value, theForm) {
			if (value === "") {
				$scope.badNHI = false;
				theForm.NHI.$setValidity("badNHI", true);
				delete $scope.patient.nhi;
				return;
			}
			if (NHI.validate(value) === true) {
				$scope.badNHI = false;
				theForm.NHI.$setValidity("badNHI", true);
				searchNHIPatients(value);
			} else {
				$scope.badNHI = true;
				theForm.NHI.$setValidity("badNHI", false);
			}
		};

		$scope.searchPatient = function () {
			PatientAPI.isProcessing = true;
			$scope.optionalPatients = [];
			$scope.clickedPatient = null;
			const dob = $scope.showingDate.YYYY + "/" + $scope.showingDate.MM + "/" + $scope.showingDate.DD;

			queryPatients($scope.patient.lastName, dob).then(
				function (resp) {
					if (resp.data) {
						$scope.optionalPatients = resp.data.map((t) => ({
							...t, firstName: (t.names && t.names[0] || {}).firstName, middleName: (t.names && t.names[0] || {}).middleName,
							lastName: (t.names && t.names[0] || {}).lastName, codedEthnicities: angular.copy(t.ethnicities), expanded: false,
						}));
						$scope.tableOptionalPatientsParams = new NgTableParams({ count: 5 }, { counts: [], dataset: $scope.optionalPatients });
					}
					modalForNHI();
				},
				function (err) {
					console.error("NHI patient query failed", err && err.status);
					$scope.alertMessage = NHI_SERVICE_ERROR.body;
					showServerError(NHI_SERVICE_ERROR.title);
				}
			).finally(function () {
				PatientAPI.isProcessing = false;
			});
		};

		$scope.selectOptionalPatient = function (patient) {
			$scope.optionalPatients.forEach(function (val) { val.expanded = false; });
			patient.expanded = true;
		};

		$scope.presetPatient = function (patient) {
			$scope.showingDate.DD = parseInt(patient.dateOfBirth.substr(8, 2));
			$scope.showingDate.MM = parseInt(patient.dateOfBirth.substr(5, 2));
			$scope.showingDate.YYYY = parseInt(patient.dateOfBirth.substr(0, 4));
			$scope.patient.lastName = patient.names[0].lastName;
			$scope.patient.dateOfBirth = makeDateString();
			$scope.patient.firstName = patient.names[0].firstName;
			$scope.patient.nhi = patient.nhi;
			$scope.patient.gender = NHISearchPatientsAPI.transformGenderProp(patient.gender);

			if (patient.contacts && patient.contacts.length > 0 && !patient.contacts[0].confidentialInformation && patient.contacts[0].phoneNumber) {
				$scope.patient.phoneNumber = patient.contacts[0].phoneNumber;
			}

			if (CONFIG.NHI_ADDRESS && patient.addresses && patient.addresses.length > 0) {
				$scope.patient.addresses = [{}];
				const address = patient.addresses[0];
				extractApartmentNumber(address);
				const add = formatAddressPostCode(address);
				PlaceAPI.getPlaceDetail($element[0].querySelector("#hidden_address"), add, function (place) {
					$scope.patient.addresses[0] = { type: "PHYSICAL", apartmentNumber: address.apartmentNumber ? address.apartmentNumber : "" };
					$scope.display.address = angular.copy(place);
					$scope.display.apartmentNumber = $scope.patient.addresses[0].apartmentNumber;
					setTimeout(() => { $scope.$apply(); }, 0);
				});
			}

			if ($scope.patientsOptionalModal) $scope.patientsOptionalModal.hide();
			if (!$scope.patient.id) CurrentPatient.createNewPatientWithPreset();
		};

		$scope.presetPatientV2 = function (patient) {
			const dateOfBirth = patient.dob.split("-");
			composeDOB(dateOfBirth);
			$scope.patient.lastName = patient.familyNameCol;
			$scope.patient.dateOfBirth = $scope.showingDate.YYYY + "/" + $scope.showingDate.MM + "/" + $scope.showingDate.DD;
			$scope.patient.firstName = patient.firstNameCol;
			$scope.patient.nhi = patient.nhiCol;
			$scope.patient.gender = NHISearchPatientsAPI.transformGenderProp(patient.gender);
			$scope.patient.isResident = getNZCitizenState(patient.nzCitizen);
			$scope.patient.codedEthnicities = patient.codedEthnicities;

			if (CONFIG.NHI_ADDRESS && patient.addresses && patient.addresses.length > 0) {
				$scope.patient.addresses = [{}];
				let address = patient.addresses.find((t) => t.addressType === "PHYSICAL") || patient.addresses[0];

				if (address && !address.addressCountry && !address.overseas) address.addressCountry = "New Zealand";

				const add = address.addressLine.join(",") + "," + address.addressCity + "," + address.addressCountry + "," + address.addressPostalCode;

				PlaceAPI.getPlaceDetail($element[0].querySelector("#hidden_address"), add, function (place) {
					$scope.patient.addresses[0] = { type: "PHYSICAL", apartmentNumber: address.apartmentNumber ? address.apartmentNumber : "" };
					$scope.display.address = angular.copy(place);
					$scope.display.apartmentNumber = $scope.patient.addresses[0].apartmentNumber;
					setTimeout(() => { $scope.$apply(); }, 0);
				});
			}
			if ($scope.patientsOptionalModal) $scope.patientsOptionalModal.hide();
			if (!$scope.patient.id) CurrentPatient.createNewPatientWithPreset();
		};

		$scope.createNewPatientUI = function () {
			if ($scope.patientsOptionalModal) $scope.patientsOptionalModal.hide();
		};

		$scope.popoverDormantNHI = function (obj) {
			return { title: "Dormant NHI", content: obj.dormantIdentifiers.join() };
		};

		$scope.trimLongText = function (text) {
			return (text && text.length > 30) ? text.substring(0, 27) + "..." : text;
		};

		$scope.formatDate = function (_date) {
			if (_date) {
				const date = new Date(_date);
				return date.getDate() + "-" + (date.getMonth() + 1) + "-" + date.getFullYear();
			}
			return "";
		};

		function queryPatients(lastName, dob) {
			return NHI_VERSION_V2 ? NHISearchPatientsAPI.queryPatientsV2(lastName, dob) : NHISearchPatientsAPI.queryPatients(lastName, dob);
		}

		function searchAllNHIPatients(nhi) {
			return NHI_VERSION_V2 ? NHISearchPatientsAPI.searchAllNHIPatientsV2(nhi) : NHISearchPatientsAPI.searchAllNHIPatients(nhi);
		}

		function searchNHIPatients(nhi) {
			if (!$scope.isNHIFeatureEnabled) return;
			PatientAPI.isProcessing = true;
			$scope.optionalPatients = [];
			$scope.clickedPatient = null;
			searchAllNHIPatients(nhi).then(
				function (resp) {
					NHI_VERSION_V2 ? renderNHIResultV2(resp) : renderNHIResultV1(resp);
					modalForNHI();
				},
				function (err) {
					console.error("NHI search failed", err && err.status);
					$scope.alertMessage = NHI_SERVICE_ERROR.body;
					showServerError(NHI_SERVICE_ERROR.title);
				}
			).finally(function () { PatientAPI.isProcessing = false; });
		}

		function modalForNHI() {
			if (NHI_VERSION_V2) {
				$mdDialog.show({
					controller: "nhiRecordController", templateUrl: "app/src/views/templates/modal.select-patient-v2.tpl.html", parent: angular.element(document.body),
					clickOutsideToClose: false, scope: $scope.$new(true), preserveScope: false,
					// Over a review dialog when the form is in one.
					multiple: true,
					resolve: { optionalPatients: () => $scope.optionalPatients, callback: () => (nhiObj) => $scope.presetPatientV2(nhiObj), skipAction: () => null },
				});
			} else {
				$scope.patientsOptionalModal = $modal({
					scope: $scope, templateUrl: "app/src/views/templates/modal.select-patient.tpl.html", show: true, title: "SUGGESTED PATIENTS FROM THE NHI SERVICE", backdrop: "static", keyboard: false,
				});
			}
		}

		function renderNHIResultV1(resp) {
			if (resp.data) {
				$scope.optionalPatients.push(resp.data);
				$scope.optionalPatients = $scope.optionalPatients.map((t) => ({
					...t, firstName: (t.names && t.names[0] || {}).firstName, middleName: (t.names && t.names[0] || {}).middleName, lastName: (t.names && t.names[0] || {}).lastName, expanded: false,
				}));
				$scope.tableOptionalPatientsParams = new NgTableParams({ count: 5 }, { counts: [], dataset: $scope.optionalPatients });
			}
		}

		function getName(nameArr) {
			// The NHI service doesn't always flag a name as preferred, and a record can come back with none.
			nameArr = nameArr || [];
			const target = nameArr.find((t) => t && t.preferred) || nameArr[0] || {};
			return { firstName: (target.givenNameList || []).join(" "), middleName: "", lastName: target.familyName || "" };
		}

		function renderNHIResultV2(resp) {
			if (resp.data) {
				$scope.optionalPatients = [];
				if (Array.isArray(resp.data)) {
					const transformed = resp.data.map((t) => ({ ...t, codedEthnicities: angular.copy(t.ethnicities) }));
					$scope.optionalPatients.push(...transformed);
				} else {
					$scope.optionalPatients.push({ ...resp.data, codedEthnicities: resp.data.ethnicities });
				}
				$scope.optionalPatients = $scope.optionalPatients.map((t) => ({ ...t, expanded: false, ...getName(t.names) }));
				$scope.tableOptionalPatientsParams = new NgTableParams({ count: 5 }, { counts: [], dataset: $scope.optionalPatients });
			}
		}

		function formatAddressPostCode(address) {
			let formattedAddress = "";
			if (address.streetAddress) formattedAddress += address.streetAddress + ",";
			if (address.suburb) formattedAddress += address.suburb + ",";
			if (address.city) formattedAddress += address.city + ",";
			if (address.postCode) formattedAddress += address.postCode;
			return formattedAddress;
		}

		function extractApartmentNumber(addressObj) {
			const streetAddress = addressObj.streetAddress;
			if (streetAddress && streetAddress.includes("/")) {
				addressObj.streetAddress = streetAddress.split("/")[1];
				addressObj.apartmentNumber = streetAddress.split("/")[0];
			}
		}

		function composeDOB(dateOfBirth) {
			if ($scope.fhirNhiFeature) {
				$scope.showingDate.DD = parseInt(dateOfBirth[0]);
				$scope.showingDate.MM = parseInt(dateOfBirth[1]);
				$scope.showingDate.YYYY = parseInt(dateOfBirth[2]);
			} else {
				$scope.showingDate.DD = parseInt(dateOfBirth[2]);
				$scope.showingDate.MM = parseInt(dateOfBirth[1]);
				$scope.showingDate.YYYY = parseInt(dateOfBirth[0]);
			}
		}

		function getNZCitizenState(nzCitizen) {
			if (nzCitizen === "YES") return true;
			if (nzCitizen === "NO") return false;
			return null;
		}

		function showServerError(title) {
			// Always cleared so a detail from an earlier failure cannot trail into this one.
			$scope.alertDetail = null;
			if ($scope.serverErrorModal) {
				$scope.serverErrorModal.hide();
			}
			$scope.serverErrorModal = $modal({ scope: $scope, templateUrl: "app/src/views/templates/modal.server-error.tpl.html", show: true, title: title || "CatTrax has encountered an error" });
		}
	},
]);
